import { useT } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_alpine-ventures/artifacts/ukynai28_Color%20logo%20-%20no%20background.png";

const FLAGS = [
    { flag: "🇫🇷", country: "France" },
    { flag: "🇱🇻", country: "Latvia" },
    { flag: "🇱🇹", country: "Lithuania" },
    { flag: "🇨🇭", country: "Switzerland" },
    { flag: "🇦🇲", country: "Armenia" },
    { flag: "🇵🇹", country: "Portugal" },
    { flag: "🇪🇪", country: "Estonia" },
    { flag: "🇭🇰", country: "Hong Kong" },
];

export default function Footer() {
    const { t } = useT();
    const f = t.footer;

    const navLinks = [
        { href: "/#about", label: t.nav.about },
        { href: "/#thesis", label: t.nav.thesis },
        { href: "/#collaboration", label: t.nav.collaboration },
        { href: "/#contact", label: t.nav.contact },
    ];

    return (
        <footer
            data-testid="footer"
            className="relative border-t border-[rgba(201,169,110,0.15)] mt-8"
        >
            {/* Subtle gold radial glow */}
            <div
                aria-hidden="true"
                className="absolute -top-px left-0 right-0 h-px"
                style={{
                    background:
                        "linear-gradient(90deg, transparent, rgba(201,169,110,0.4), transparent)",
                }}
            />

            <div className="max-w-[1280px] mx-auto px-6 md:px-12 pt-16 pb-10">
                {/* Top: 4 columns */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
                    {/* Col 1 — Identity */}
                    <div className="md:col-span-5">
                        <Link
                            to="/"
                            aria-label="Alpe.vc — home"
                            className="inline-block"
                        >
                            <img
                                src={LOGO_URL}
                                alt="Alpe.vc"
                                className="h-9 w-auto"
                                draggable="false"
                            />
                        </Link>
                        <p className="mt-6 text-[14px] leading-[1.7] text-[rgba(232,230,223,0.7)] max-w-[340px]">
                            {f.initiative}
                        </p>
                        <address className="mt-4 not-italic text-[13px] leading-[1.7] text-[rgba(232,230,223,0.5)]">
                            {f.address}
                            <br />
                            <a
                                href="mailto:info@alpe.vc"
                                data-testid="footer-email"
                                className="text-[#C9A96E] hover:text-[#d4b986] transition-colors"
                            >
                                info@alpe.vc
                            </a>
                        </address>
                    </div>

                    {/* Col 2 — Navigation */}
                    <div className="md:col-span-3">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-[#C9A96E] font-medium mb-5">
                            {f.colNavigation}
                        </p>
                        <ul className="space-y-3 text-[14px]">
                            {navLinks.map((l) => (
                                <li key={l.href}>
                                    <a
                                        href={l.href}
                                        className="text-[rgba(232,230,223,0.7)] hover:text-[#E8E6DF] transition-colors"
                                    >
                                        {l.label}
                                    </a>
                                </li>
                            ))}
                            <li>
                                <a
                                    href="/#contact"
                                    data-testid="footer-submit"
                                    className="inline-flex items-center gap-1 text-[#C9A96E] hover:text-[#d4b986] transition-colors mt-1"
                                >
                                    {t.nav.cta}
                                    <span aria-hidden="true">→</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Col 3 — Network & Ecosystem */}
                    <div className="md:col-span-4">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-[#C9A96E] font-medium mb-5">
                            {f.colEcosystem}
                        </p>
                        <p className="text-[14px] leading-[1.7] text-[rgba(232,230,223,0.7)]">
                            {f.ecosystemA}
                        </p>
                        <p className="mt-3 text-[14px] leading-[1.7] text-[rgba(232,230,223,0.7)]">
                            {f.ecosystemB}
                        </p>
                        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-3">
                            <div className="flex flex-wrap items-center gap-2 text-[18px]">
                                {FLAGS.map((fl) => (
                                    <span
                                        key={fl.country}
                                        title={fl.country}
                                        aria-label={fl.country}
                                    >
                                        {fl.flag}
                                    </span>
                                ))}
                            </div>
                            <span className="text-[11px] text-[rgba(232,230,223,0.45)] italic">
                                {f.comingNote}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Bottom line */}
                <div className="mt-14 pt-6 border-t border-[rgba(201,169,110,0.12)] flex flex-col md:flex-row gap-3 md:gap-0 md:items-center md:justify-between text-[12px] text-[rgba(232,230,223,0.45)]">
                    <p>{f.rights}</p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <Link
                            to="/legal"
                            data-testid="footer-link-legal"
                            className="hover:text-[#C9A96E] transition-colors"
                        >
                            {f.legalLink}
                        </Link>
                        <span className="text-[#C9A96E]/30">·</span>
                        <Link
                            to="/privacy"
                            data-testid="footer-link-privacy"
                            className="hover:text-[#C9A96E] transition-colors"
                        >
                            {f.privacyLink}
                        </Link>
                        <span className="text-[#C9A96E]/30">·</span>
                        <span>{f.hostedOn}</span>
                        <span className="text-[#C9A96E]/30">·</span>
                        <span>{f.dataController}</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
